import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { productApi, companyApi, type Product, type ProductStatRow } from '../../lib/api';
import { useCompany } from '../CompanyProvider';
import { useConfirm } from '../ConfirmProvider';
import { useToast } from '../ToastProvider';
import { Button, Card, EmptyState, ErrorNote, Field, PageHeader, euro } from '../ui';

// Draft fuer den Editor: Preise als Text-Eingabe (Euro).
interface Draft {
  id?: string;
  name: string;
  sku: string;
  description: string;
  category: string;
  unit: string;
  unitPrice: string;      // Euro
  purchasePrice: string;  // Euro (optional)
  taxRate: string;
  trackStock: boolean;
  stockQty: string;
  lowStockThreshold: string;
  active?: boolean;
}

const emptyDraft = (): Draft => ({
  name: '', sku: '', description: '', category: '', unit: 'Stück',
  unitPrice: '', purchasePrice: '', taxRate: '19',
  trackStock: false, stockQty: '0', lowStockThreshold: '',
});

function toCents(s: string): number {
  const n = parseFloat(s.replace(',', '.'));
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}
function fromCents(c: number | null | undefined): string {
  return c == null ? '' : (c / 100).toFixed(2).replace('.', ',');
}

// Marge in Prozent (auf VK netto) aus VK/EK-Cent.
function marginPct(sell: number, buy: number | null): number | null {
  if (buy == null || sell <= 0) return null;
  return Math.round(((sell - buy) / sell) * 100);
}

export default function Products(): ReactElement {
  const { active } = useCompany();
  const confirm = useConfirm();
  const toast = useToast();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Verkaufsstatistik (Bestseller) je Jahr.
  const [stats, setStats] = useState<ProductStatRow[]>([]);
  const [statYear, setStatYear] = useState(new Date().getFullYear());

  useEffect(() => {
    if (!active) return;
    companyApi.usage(active.id).then((u) => setLocked(!u.features.includes('produkte'))).catch(() => undefined);
  }, [active]);

  async function load() {
    if (!active) return;
    setLoading(true);
    try {
      setProducts(await productApi.list(active.id, { includeInactive: true }));
    } catch {
      /* bei gesperrtem Feature still */
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useEffect(() => {
    if (!active) return;
    productApi.stats(active.id, statYear).then((r) => setStats(r.stats)).catch(() => setStats([]));
  }, [active, statYear]);

  const categories = useMemo(
    () => Array.from(new Set(products.map((p) => p.category?.trim()).filter(Boolean))).sort() as string[],
    [products],
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      if (!showInactive && !p.active) return false;
      if (catFilter && p.category !== catFilter) return false;
      if (q) {
        const hay = [p.name, p.sku, p.description, p.category].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [products, search, catFilter, showInactive]);

  function openEditor(p?: Product) {
    setError(null);
    if (!p) { setDraft(emptyDraft()); return; }
    setDraft({
      id: p.id, name: p.name, sku: p.sku ?? '', description: p.description ?? '',
      category: p.category ?? '', unit: p.unit, unitPrice: fromCents(p.unitPriceCents),
      purchasePrice: fromCents(p.purchasePriceCents), taxRate: String(Number(p.taxRate)),
      trackStock: p.trackStock, stockQty: String(p.stockQty),
      lowStockThreshold: p.lowStockThreshold != null ? String(p.lowStockThreshold) : '',
      active: p.active,
    });
  }

  async function save() {
    if (!active || !draft) return;
    if (!draft.name.trim()) { setError(new Error('Bitte einen Namen angeben.')); return; }
    setBusy(true);
    setError(null);
    try {
      const input: Partial<Product> = {
        name: draft.name.trim(),
        sku: draft.sku.trim() || null,
        description: draft.description.trim() || null,
        category: draft.category.trim() || null,
        unit: draft.unit.trim() || 'Stück',
        unitPriceCents: toCents(draft.unitPrice),
        purchasePriceCents: draft.purchasePrice.trim() ? toCents(draft.purchasePrice) : null,
        taxRate: draft.taxRate as unknown as string,
        trackStock: draft.trackStock,
        stockQty: parseInt(draft.stockQty || '0', 10) || 0,
        lowStockThreshold: draft.trackStock && draft.lowStockThreshold.trim() ? (parseInt(draft.lowStockThreshold, 10) || 0) : null,
      };
      if (draft.id) await productApi.update(active.id, draft.id, input);
      else await productApi.create(active.id, input);
      toast.success(draft.id ? 'Produkt gespeichert.' : 'Produkt angelegt.');
      setDraft(null);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    if (!active) return;
    const ok = await confirm({ title: 'Produkt deaktivieren?', message: 'Das Produkt wird ausgeblendet. Bestehende Rechnungspositionen bleiben erhalten.', confirmLabel: 'Deaktivieren' });
    if (!ok) return;
    await productApi.remove(active.id, id);
    toast.info('Produkt deaktiviert.');
    await load();
  }

  if (locked) {
    return (
      <div className="mx-auto max-w-4xl">
        <PageHeader title="Produkte & Dienstleistungen" subtitle="Artikelstamm pflegen und mit einem Klick in Rechnungen einfügen." />
        <Card className="text-center">
          <p className="text-gray-700 dark:text-gray-300">🔒 Produkte & Dienstleistungen sind ab dem <strong>Professional</strong>-Tarif verfügbar.</p>
          <Link to="/app/einstellungen/tarif" className="mt-2 inline-block font-medium text-blue-600 hover:underline">Tarif ansehen</Link>
        </Card>
      </div>
    );
  }

  // Draft-Marge live.
  const draftMargin = draft ? marginPct(toCents(draft.unitPrice), draft.purchasePrice.trim() ? toCents(draft.purchasePrice) : null) : null;

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title="Produkte & Dienstleistungen"
        subtitle="Artikelstamm mit Preis, Steuersatz und Marge – mit einem Klick in Rechnungen."
        action={<Button onClick={() => openEditor()}>+ Neues Produkt</Button>}
      />

      {products.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suchen (Name, Artikelnr., Kategorie …)"
            className="min-w-[200px] flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
          />
          {categories.length > 0 && (
            <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100">
              <option value="">Alle Kategorien</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 dark:border-gray-700 dark:text-gray-300">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="h-4 w-4" />
            Inaktive
          </label>
        </div>
      )}

      {/* Bestseller / Verkaufsstatistik */}
      {stats.length > 0 && (
        <Card className="mb-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Bestseller {statYear}</h3>
            <select value={statYear} onChange={(e) => setStatYear(Number(e.target.value))}
              className="rounded-lg border border-gray-300 px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100">
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase text-gray-400">
                <tr>
                  <th className="py-1">Produkt</th>
                  <th className="py-1 text-right">Menge</th>
                  <th className="py-1 text-right">Umsatz (netto)</th>
                  <th className="py-1 text-right">Deckungsbeitrag</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {stats.slice(0, 10).map((s) => (
                  <tr key={s.productId}>
                    <td className="py-1.5 pr-2">{s.name}</td>
                    <td className="py-1.5 text-right">{s.qty.toLocaleString('de-DE')}</td>
                    <td className="py-1.5 text-right font-medium">{euro(s.revenueCents)}</td>
                    <td className={`py-1.5 text-right ${s.marginCents < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{euro(s.marginCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {loading ? (
        <p className="text-gray-500">Lädt …</p>
      ) : products.length === 0 ? (
        <EmptyState
          title="Noch keine Produkte"
          hint="Lege deine meistverkauften Produkte und Dienstleistungen an, um Rechnungen schneller zu schreiben."
          action={<Button onClick={() => openEditor()}>+ Neues Produkt</Button>}
        />
      ) : visible.length === 0 ? (
        <Card className="text-center text-sm text-gray-500">Keine Produkte für diese Filter.</Card>
      ) : (
        <div className="space-y-2">
          {visible.map((p) => {
            const m = marginPct(p.unitPriceCents, p.purchasePriceCents);
            return (
              <Card key={p.id} className={`flex items-center justify-between !p-4 ${!p.active ? 'opacity-60' : ''}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 font-medium text-gray-900 dark:text-gray-100">
                    <span className="truncate">{p.name}</span>
                    {p.sku && <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">{p.sku}</span>}
                    {!p.active && <span className="shrink-0 rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-500">inaktiv</span>}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-500">
                    <span>{euro(p.unitPriceCents)} / {p.unit} · {Number(p.taxRate)}% USt</span>
                    {p.category && <span className="rounded bg-blue-50 px-1.5 py-0.5 text-xs text-blue-700">{p.category}</span>}
                    {m !== null && <span className={`text-xs ${m < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>Marge {m}%</span>}
                    {p.trackStock && (() => {
                      const low = p.lowStockThreshold != null && p.stockQty <= p.lowStockThreshold;
                      return (
                        <span className={`rounded px-1.5 py-0.5 text-xs ${low ? 'bg-rose-100 text-rose-700' : 'bg-gray-100 text-gray-600'}`}>
                          Bestand: {p.stockQty}{low ? ' ⚠ niedrig' : ''}
                        </span>
                      );
                    })()}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {p.trackStock && (
                    <div className="flex items-center gap-1">
                      <button onClick={() => active && productApi.adjustStock(active.id, p.id, -1).then(load)} className="h-7 w-7 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50" aria-label="Bestand -1">−</button>
                      <button onClick={() => active && productApi.adjustStock(active.id, p.id, 1).then(load)} className="h-7 w-7 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50" aria-label="Bestand +1">+</button>
                    </div>
                  )}
                  <Button variant="secondary" onClick={() => openEditor(p)}>Bearbeiten</Button>
                  {p.active && <Button variant="ghost" onClick={() => remove(p.id)}>Deaktivieren</Button>}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {draft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-auto rounded-2xl bg-white p-6">
            <h2 className="mb-4 text-lg font-bold text-gray-900">{draft.id ? 'Produkt bearbeiten' : 'Neues Produkt'}</h2>
            <div className="space-y-4">
              <ErrorNote error={error} />
              <Field label="Name" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} placeholder="z. B. Beratungsstunde" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Artikelnummer (optional)" value={draft.sku} onChange={(v) => setDraft({ ...draft, sku: v })} placeholder="z. B. ART-001" />
                <Field label="Kategorie" value={draft.category} onChange={(v) => setDraft({ ...draft, category: v })} placeholder="z. B. Dienstleistung" />
              </div>
              <Field label="Beschreibung" value={draft.description} onChange={(v) => setDraft({ ...draft, description: v })} placeholder="Erscheint als Positionstext" />
              <div className="grid grid-cols-3 gap-3">
                <Field label="Verkaufspreis €" value={draft.unitPrice} onChange={(v) => setDraft({ ...draft, unitPrice: v })} placeholder="0,00" />
                <Field label="Einkaufspreis €" value={draft.purchasePrice} onChange={(v) => setDraft({ ...draft, purchasePrice: v })} placeholder="optional" hint="für Marge" />
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  USt %
                  <select value={draft.taxRate} onChange={(e) => setDraft({ ...draft, taxRate: e.target.value })}
                    className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5 text-base dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100">
                    <option value="19">19%</option>
                    <option value="7">7%</option>
                    <option value="0">0%</option>
                  </select>
                </label>
              </div>
              <Field label="Einheit" value={draft.unit} onChange={(v) => setDraft({ ...draft, unit: v })} placeholder="z. B. Stück, Stunde" />
              {draftMargin !== null && (
                <div className={`rounded-xl px-3 py-2 text-sm ${draftMargin < 0 ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700'}`}>
                  Deckungsbeitrag: {euro(toCents(draft.unitPrice) - toCents(draft.purchasePrice))} · Marge {draftMargin}%
                  {draftMargin < 0 && ' — Verkaufspreis unter Einkaufspreis!'}
                </div>
              )}

              {/* Bestandsfuehrung */}
              <div className="rounded-xl border border-gray-200 p-3">
                <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={draft.trackStock} onChange={(e) => setDraft({ ...draft, trackStock: e.target.checked })} className="h-4 w-4" />
                  Lagerbestand führen
                </label>
                {draft.trackStock && (
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <Field label="Aktueller Bestand" value={draft.stockQty} onChange={(v) => setDraft({ ...draft, stockQty: v })} placeholder="0" />
                    <Field label="Warnung ab Bestand" value={draft.lowStockThreshold} onChange={(v) => setDraft({ ...draft, lowStockThreshold: v })} placeholder="z. B. 5" hint="leer = keine Warnung" />
                  </div>
                )}
                {draft.trackStock && <p className="mt-2 text-xs text-gray-400">Der Bestand sinkt automatisch, sobald eine Rechnung mit diesem Produkt versendet/bezahlt wird; Storno bucht zurück.</p>}
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDraft(null)}>Abbrechen</Button>
              <Button onClick={save} busy={busy}>Speichern</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
